import styled from "styled-components";
import AppleLogin from "react-apple-login";
import { useMediaQuery } from "react-responsive";
import FacebookLogin from "@greatsumini/react-facebook-login";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";

import ModalWindow from "components/other/ModalWindow";
import InputText from "components/forms/InputText";
import SocialButton from "components/buttons/SocialButton";
import ButtonPrimary from "components/buttons/ButtonPrimary";
import InputWithLabel from "components/forms/InputWithLabel";

import scalelupLogo from "assets/img/logo/scalelup.svg";
import emailIcon from "assets/img/icons/envelope-icon-gold.svg";

const Login = styled.div`
  width: 100%;

  color: #ffffff;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LoginForm = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoWrap = styled.div`
  width: 150px;
  padding: 5px 0 20px;
`;

const Heading = styled.div`
  font-size: 24px;
`;

const Details = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  text-align: center;
`;

const SocialButtons = styled.div`
  margin-top: 30px;
  width: 100%;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${css.BUTTONS_GAP};

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
  }
`;

const DelimiterWrap = styled.div`
  margin-top: 20px;
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DelimiterLine = styled.div`
  width: 45%;

  border-bottom: 1px solid ${css.TEXT_GREY};
`;

const DelimiterText = styled.div`
  font-size: 11px;
  color: ${css.TEXT_GREY};
`;

const EmailForm = styled.div`
  width: 100%;
  margin-top: 20px;

  display: flex;
  flex-direction: column;
`;

const EmailFormControls = styled.div`
  width: 100%;
  margin-top: 20px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ForgotPasswordLink = styled.div`
  font-weight: 500;
  color: ${css.LIGHT_BLUE};

  transition: 0.2s;

  &:hover {
    cursor: pointer;
    filter: brightness(110%);
  }
`;

const SignUpInfo = styled.div`
  margin-top: 30px;
`;

const SignUpLink = styled.span`
  padding-left: 1px;

  font-weight: 500;
  color: ${css.LIGHT_BLUE};

  transition: 0.2;

  &:hover {
    cursor: pointer;
    filter: brightness(110%);
  }
`;

const ErrorText = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN};
  max-width: 340px;

  font-weight: 500;
  color: ${css.ERROR_RED};
`;

const ResentInfo = styled.div`
  margin-top: 15px;
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ResendLink = styled.div`
  margin-top: 10px;

  font-weight: 500;
  color: ${css.LIGHT_BLUE};

  transition: 0.2s;

  &:hover {
    cursor: pointer;
    filter: brightness(110%);
  }
`;

const SuccessText = styled.div`
  margin-top: 10px;

  font-size: 20px;
  font-weight: 500;
  text-align: center;
  line-height: 125%;
`;

const EmailInfo = styled.div`
  width: 100%;
  max-width: 380px;
  margin-top: 40px;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  border-radius: ${css.BOX_RADIUS_SECONDARY};
  background-color: ${css.FLAT_BOX_BACKGROUND};

  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const EmailInfoTop = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  gap: 20px;
`;

const EmailInfoIconWrap = styled.div`
  padding: 0 10px;
`;

const EmailInfoIcon = styled.div`
  width: 40px;
  height: 40px;
`;

const EmailInfoText = styled.div``;

const EmailInfoSpamInfo = styled.div`
  font-size: 13px;
  text-align: center;
  line-height: 125%;
`;

const CloseButtonWrap = styled.div`
  margin-top: 35px;
`;

export default (props) => {
  const {
    handleModalClose,
    credentials,
    warnings,
    handleInputChange,
    loginMessage,
    showResendLink,
    verificationResent,
    submitLoginForm,
    loadingLogin,
    onResendVerification,
    emailToBeVerified,
    handleKeyDown,
    loginGoogle,
    loginApple,
    openSignup,
    openForgot,
    loginFacebook,
  } = props;

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  return (
    <ModalWindow
      modal="login"
      handleModalClose={handleModalClose}
      fullScreenHeight={true}
    >
      <Login>
        {!verificationResent ? (
          <LoginForm>
            <LogoWrap>
              <img src={scalelupLogo} style={{ width: "100%" }} />
            </LogoWrap>
            <Heading>Welcome back</Heading>
            <Details>Please enter your details to sign in.</Details>
            <SocialButtons>
              <AppleLogin
                clientId="com.scalelup.service"
                redirectURI="https://scalelup.com"
                usePopup={true}
                callback={loginApple}
                scope="email name"
                responseMode="query"
                render={(renderProps) => (
                  <SocialButton
                    social="apple"
                    big={mobileScreen}
                    onClick={renderProps.onClick}
                  />
                )}
              />
              <SocialButton
                social="google"
                big={mobileScreen}
                onClick={loginGoogle}
              />
              <FacebookLogin
                appId="1596483107932488"
                onSuccess={(response) => {
                  loginFacebook(response);
                }}
                onFail={(error) => {
                  //console.log("Login Failed!", error);
                }}
                render={({ onClick, logout }) => (
                  <SocialButton
                    social="facebook"
                    big={mobileScreen}
                    onClick={onClick}
                  />
                )}
              />
            </SocialButtons>
            <DelimiterWrap>
              <DelimiterLine />
              <DelimiterText>OR</DelimiterText>
              <DelimiterLine />
            </DelimiterWrap>
            <EmailForm>
              <InputWithLabel label="E-Mail Address" width="100%">
                <InputText
                  type="email"
                  name="email"
                  value={credentials.email}
                  placeholder="Enter your email"
                  onChange={handleInputChange}
                  maxLength={100}
                  warning={warnings.includes("email")}
                  onKeyDown={handleKeyDown}
                />
              </InputWithLabel>
              <InputWithLabel
                label="Password"
                width="100%"
                marginTop={css.VERTICAL_BOX_MARGIN}
              >
                <InputText
                  type="password"
                  name="password"
                  value={credentials.password}
                  placeholder="Enter your password"
                  onChange={handleInputChange}
                  maxLength={100}
                  warning={warnings.includes("password")}
                  onKeyDown={handleKeyDown}
                />
              </InputWithLabel>
              {loginMessage && <ErrorText>{loginMessage}</ErrorText>}
              {showResendLink && (
                <ResendLink onClick={onResendVerification}>
                  Resend email
                </ResendLink>
              )}
              <EmailFormControls>
                <ButtonPrimary
                  onClick={submitLoginForm}
                  disabled={loadingLogin}
                >
                  Sign In
                </ButtonPrimary>
                <ForgotPasswordLink onClick={() => openForgot()}>
                  Forgot password?
                </ForgotPasswordLink>
              </EmailFormControls>
              <SignUpInfo>
                Don't have an account yet?{" "}
                <SignUpLink onClick={() => openSignup()}>Sign Up</SignUpLink>
              </SignUpInfo>
            </EmailForm>
          </LoginForm>
        ) : (
          <ResentInfo>
            <LogoWrap>
              <img src={scalelupLogo} style={{ width: "100%" }} />
            </LogoWrap>
            <SuccessText>
              A verification email has been re-sent to {emailToBeVerified}
            </SuccessText>
            <EmailInfo>
              <EmailInfoTop>
                <EmailInfoIconWrap>
                  <EmailInfoIcon>
                    <img src={emailIcon} width="100%" alt="Icon" />
                  </EmailInfoIcon>
                </EmailInfoIconWrap>
                <EmailInfoText>
                  We have sent the activation link to your email address. Please
                  follow the link in the email to log in to your new account.
                </EmailInfoText>
              </EmailInfoTop>
              <EmailInfoSpamInfo>
                If you cannot find the activation email in your inbox, please
                check your SPAM folder.
              </EmailInfoSpamInfo>
            </EmailInfo>
            <CloseButtonWrap>
              <ButtonPrimary onClick={handleModalClose}>Close</ButtonPrimary>
            </CloseButtonWrap>
          </ResentInfo>
        )}
      </Login>
    </ModalWindow>
  );
};
